import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { Text } from '@kerfjs/ui/text';
import { type SafeHtml } from 'kerfjs';

import type { DecisionT, LineOrderT } from '../../../data/api.js';
import { demandSavings, orderDialog, quoteMatrix, receiptDialog, workspace } from '../../../data/state.js';
import { demandDecisionsLocked } from '../../../lib/context.js';
import { OrderDialog } from './order-dialog.js';
import { OutcomeRow } from './outcome-row.js';
import { ReceiptDialog } from './receipt-dialog.js';
import type { SummaryLine } from './summary-context.js';
import { UndecidedLines } from './undecided-lines.js';

/** Vendors that quoted a given line, for the outcome's "final vendor" override. */
function outcomeVendorOptions(lineId: string): { vendorId: string; vendorName: string }[] {
  const matrix = quoteMatrix.value;
  if (matrix === null) return [];
  const seen = new Set<string>();
  const options: { vendorId: string; vendorName: string }[] = [];
  for (const column of matrix.columns) {
    if (column.vendorId === null || seen.has(column.vendorId)) continue;
    if (matrix.cells[lineId]?.[column.quotationId] == null) continue;
    seen.add(column.vendorId);
    options.push({ vendorId: column.vendorId, vendorName: column.vendorName });
  }
  return options;
}

/** Resolve the vendor selected by an existing decision from its chosen quote line. */
function decisionVendorId(lineId: string, decision: DecisionT): string {
  const matrix = quoteMatrix.value;
  if (matrix === null || decision.chosenQuoteLineId === null) return '';
  for (const column of matrix.columns) {
    const cell = matrix.cells[lineId]?.[column.quotationId];
    if (cell?.quoteLineId === decision.chosenQuoteLineId && column.vendorId !== null) return column.vendorId;
  }
  return '';
}

export function Outcomes({
  lines,
  current,
  base,
}: {
  lines: SummaryLine[];
  current: Map<string, DecisionT>;
  base: string;
}): SafeHtml {
  const savingsByLine = new Map((demandSavings.value?.lines ?? []).map((line) => [line.lineItemId, line]));
  const undecided = lines.filter((line) => !current.has(line.id));
  const orders = new Map(((workspace.value?.orders ?? []) as LineOrderT[]).map((order) => [order.lineItemId, order]));
  const dialogLine = lines.find((line) => line.id === orderDialog.value?.lineId);
  const dialogDecision = dialogLine === undefined ? undefined : current.get(dialogLine.id);
  const receiptLine = lines.find((line) => line.id === receiptDialog.value?.lineId);
  const receiptOrder = receiptLine === undefined ? undefined : orders.get(receiptLine.id);
  return (
    <List gap="xs">
      {current.size === 0 ? (
        <ListInsetControl sides="rl">
          <Text tone="quiet" size="compact">
            Record decisions from the line-item menu before adding purchase outcomes.
          </Text>
        </ListInsetControl>
      ) : null}
      {lines.map((line) => {
        const decision = current.get(line.id);
        if (decision === undefined) return null;
        return (
          <OutcomeRow
            line={line}
            decision={decision}
            savings={savingsByLine.get(line.id)}
            base={demandSavings.value?.base ?? base}
            vendorOptions={outcomeVendorOptions(line.id)}
            preselectedVendor={orders.get(line.id)?.vendorId ?? decisionVendorId(line.id, decision)}
            locked={demandDecisionsLocked()}
            order={orders.get(line.id)}
          />
        );
      })}
      <UndecidedLines lines={undecided} />
      {dialogLine !== undefined && dialogDecision !== undefined ? (
        <OrderDialog
          lineNumber={dialogLine.lineNumber}
          lineDescription={dialogLine.description}
          order={orders.get(dialogLine.id)}
          vendorOptions={outcomeVendorOptions(dialogLine.id)}
          defaultVendorId={decisionVendorId(dialogLine.id, dialogDecision)}
          today={new Date().toISOString().slice(0, 10)}
          failing={orderDialog.value?.failing === true}
        />
      ) : null}
      {receiptLine !== undefined && receiptOrder !== undefined ? (
        <ReceiptDialog
          lineNumber={receiptLine.lineNumber}
          lineDescription={receiptLine.description}
          order={receiptOrder}
          today={new Date().toISOString().slice(0, 10)}
        />
      ) : null}
    </List>
  );
}
